import React, { useState, useRef, Component } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, DownloadIcon, ShieldCheckIcon, BookOpenIcon, UsersIcon, ClockIcon, AwardIcon, ChevronDownIcon, MailIcon, PhoneIcon, FileTextIcon, BarChart3Icon, LeafIcon, FlameIcon, CheckIcon, AlertTriangleIcon, BriefcaseIcon, GraduationCapIcon, LayersIcon, LandmarkIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { programs } from './courseData';
import { navigate } from '../utils/navigate';
import { MAILTO } from '../utils/mailto';
/* ─── Fade-in wrapper ─── */
function FadeIn({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} className={className} initial={{
    opacity: 0,
    y: 24
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-1673">

      {children}
    </motion.div>;
}
/* ════════════════════════════════════════
   1. HERO — light bg matching DMRVHero
════════════════════════════════════════ */
function HeroSection() {
  return <section className="relative w-full overflow-hidden bg-white" data-id="element-1674">
      {/* Subtle grid texture */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.04]" aria-hidden="true" data-id="element-1675">

        <defs data-id="element-1676">
          <pattern id="hero-grid" width="50" height="50" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-1677">

            <line x1="0" y1="0" x2="0" y2="50" stroke="#1B4332" strokeWidth="0.5" data-id="element-1678" />

            <line x1="0" y1="0" x2="50" y2="0" stroke="#1B4332" strokeWidth="0.5" data-id="element-1679" />

          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hero-grid)" data-id="element-1680" />
      </svg>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36" data-id="element-1681">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-1682">
          {/* Left: Text */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1683">

            <div className="flex items-center gap-3 mb-4" data-id="element-1684">
              <img src="https://f000.backblazeb2.com/file/jpgshared/fEKflaIh" alt="CarbonSafe" className="h-6 w-auto opacity-60" data-id="element-1685" />

              <span className="text-carbon-mid/30 text-sm" data-id="element-1686">|</span>
              <p className="text-sm font-semibold uppercase tracking-widest text-carbon-bright" data-id="element-1687">
                Training & Certification
              </p>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-deep tracking-tight leading-[1.1]" data-id="element-1688">
              Compliance-Grade ESG &{' '}
              <span className="relative inline-block" data-id="element-1689">
                <span className="relative z-10" data-id="element-1690">Climate Certification</span>
                <span className="absolute bottom-1 left-0 right-0 h-[40%] bg-carbon-bright/25 -z-0 rounded-sm" aria-hidden="true" data-id="element-1691" />

              </span>
            </h1>

            <p className="mt-6 text-lg text-carbon-mid leading-relaxed max-w-xl" data-id="element-1692">
              Prepare your institution for IFRS Sustainability Standards, EU
              CSRD, CBAM, PCAF financed emissions, and climate disclosure
              regulations. Audit-defensible competency, not awareness training.
            </p>

            {/* Compliance badge pills */}
            <div className="mt-5 flex flex-wrap gap-3" data-id="element-1693">
              {['IFRS S1/S2', 'CSRD', 'PCAF', 'Basel IV', 'CBAM', 'TCFD'].map(badge => <span key={badge} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-1694">

                    <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-1695" />
                    {badge}
                  </span>)}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4" data-id="element-1696">
              <a href="#programs" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-carbon-bright text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-carbon-bright/20" data-id="element-1697">

                View Programs
                <ArrowRightIcon className="w-4 h-4" data-id="element-1698" />
              </a>
              <a href={MAILTO.trainingBrochure} className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border-2 border-carbon-bright text-carbon-deep text-base font-semibold rounded-lg hover:bg-carbon-bright/5 transition-colors" data-id="element-1699">

                <DownloadIcon className="w-4 h-4" data-id="element-1700" />
                Download Brochure
              </a>
            </div>
          </motion.div>

          {/* Right: Training image */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} className="relative" data-id="element-1701">

            <div className="relative overflow-hidden rounded-2xl border border-carbon-geo shadow-xl shadow-carbon-deep/8" data-id="element-1702">
              <img src="https://www.swisscontact.org/_Resources/Persistent/f/0/f/7/f0f7023d4f1ffef3b2c46d05fbb3b863a239aa0f/AGI%20Training-118-5472x3645.jpg" alt="Institutional compliance training session" className="w-full h-[380px] lg:h-[440px] object-cover" data-id="element-1703" />

              <div className="absolute inset-0 bg-gradient-to-t from-carbon-deep/30 via-transparent to-transparent" data-id="element-1704" />
              {/* Stat overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between gap-3" data-id="element-1705">
                <div className="bg-white/95 border border-carbon-geo px-4 py-3 rounded-xl shadow-sm" data-id="element-1706">
                  <p className="text-xl font-bold text-carbon-bright" data-id="element-1707">$1,800</p>
                  <p className="text-xs text-carbon-mid mt-0.5" data-id="element-1708">
                    Per participant · All programs
                  </p>
                </div>
                <div className="bg-white/95 border border-carbon-geo px-4 py-3 rounded-xl shadow-sm" data-id="element-1709">
                  <p className="text-xl font-bold text-carbon-deep" data-id="element-1710">6</p>
                  <p className="text-xs text-carbon-mid mt-0.5" data-id="element-1711">
                    Certification programs
                  </p>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-3 -right-3 w-24 h-24 rounded-2xl bg-carbon-bright/10 -z-10" aria-hidden="true" data-id="element-1712" />

          </motion.div>
        </div>
      </div>
    </section>;
}
/* ════════════════════════════════════════
   2. COURSE PROGRAMS GRID
════════════════════════════════════════ */
function ProgramsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const [selectedProgram, setSelectedProgram] = useState<string | null>(null);
  const levelColors: Record<string, string> = {
    Foundational: 'text-blue-700 bg-blue-50 border-blue-200',
    Intermediate: 'text-amber-700 bg-amber-50 border-amber-200',
    Advanced: 'text-red-700 bg-red-50 border-red-200',
    Executive: 'text-purple-700 bg-purple-50 border-purple-200'
  };
  return <section id="programs" ref={ref} className="w-full bg-white py-20 sm:py-28 border-t border-carbon-geo" data-id="element-1713">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1714">
        <FadeIn className="text-center mb-14" data-id="element-1715">
          <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-1716">
            Certification Programs
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-deep tracking-tight" data-id="element-1717">
            Six Structured Programs.{' '}
            <span className="text-carbon-bright" data-id="element-1718">One Compliance Standard.</span>
          </h2>
          <p className="mt-4 text-lg text-carbon-mid max-w-2xl mx-auto" data-id="element-1719">
            Each program is built around a specific regulatory framework and
            industry context. All certifications are priced at{' '}
            <strong className="text-carbon-deep" data-id="element-1720">
              $1,800 USD per participant
            </strong>
            . Corporate cohort pricing available on request.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1721">
          {programs.map((program, i) => <motion.article key={program.id} className="bg-white rounded-2xl border border-carbon-geo flex flex-col overflow-hidden" initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.08 + i * 0.07,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1722">

              {/* Card header */}
              <div className="p-6 border-b border-carbon-geo" data-id="element-1723">
                <div className="flex items-start justify-between gap-3 mb-4" data-id="element-1724">
                  <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center flex-shrink-0" data-id="element-1725">
                    <div className="text-carbon-bright" data-id="element-1726">{program.icon}</div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 border rounded-full ${levelColors[program.level]}`} data-id="element-1727">

                    {program.level}
                  </span>
                </div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-carbon-bright mb-1" data-id="element-1728">
                  {program.category}
                </p>
                <h3 className="text-base font-bold text-carbon-deep leading-snug" data-id="element-1729">
                  {program.title}
                </h3>
                <p className="mt-2 text-sm text-carbon-mid leading-relaxed" data-id="element-1730">
                  {program.description}
                </p>
              </div>

              {/* Card meta */}
              <div className="px-6 py-4 border-b border-carbon-geo grid grid-cols-3 gap-3" data-id="element-1731">
                <div data-id="element-1732">
                  <p className="text-[10px] text-carbon-mid/60 uppercase tracking-wider font-semibold" data-id="element-1733">
                    Duration
                  </p>
                  <p className="text-sm font-bold text-carbon-deep mt-0.5" data-id="element-1734">
                    {program.duration}
                  </p>
                </div>
                <div data-id="element-1735">
                  <p className="text-[10px] text-carbon-mid/60 uppercase tracking-wider font-semibold" data-id="element-1736">
                    CPD Hours
                  </p>
                  <p className="text-sm font-bold text-carbon-deep mt-0.5" data-id="element-1737">
                    {program.cpdHours}h
                  </p>
                </div>
                <div data-id="element-1738">
                  <p className="text-[10px] text-carbon-mid/60 uppercase tracking-wider font-semibold" data-id="element-1739">
                    Price
                  </p>
                  <p className="text-sm font-bold text-carbon-bright mt-0.5" data-id="element-1740">
                    ${program.price.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Expandable modules */}
              <div className="px-6 py-4 flex-1" data-id="element-1741">
                <button onClick={() => setSelectedProgram(selectedProgram === program.id ? null : program.id)} className="flex items-center justify-between w-full text-left" data-id="element-1742">

                  <span className="text-xs font-bold uppercase tracking-wider text-carbon-mid" data-id="element-1743">
                    {selectedProgram === program.id ? 'Hide' : 'View'} Modules (
                    {program.modules.length})
                  </span>
                  <ChevronDownIcon className={`w-4 h-4 text-carbon-mid/50 transition-transform duration-200 ${selectedProgram === program.id ? 'rotate-180' : ''}`} data-id="element-1744" />

                </button>

                {selectedProgram === program.id && <motion.ul className="mt-3 space-y-2" initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} transition={{
              duration: 0.3
            }} data-id="element-1745">

                    {program.modules.map((mod, idx) => <li key={idx} className="flex items-start gap-2" data-id="element-1746">
                        <CheckIcon className="w-3.5 h-3.5 text-carbon-bright mt-0.5 flex-shrink-0" data-id="element-1747" />
                        <span className="text-xs text-carbon-mid leading-snug" data-id="element-1748">
                          {mod.title}
                        </span>
                      </li>)}
                  </motion.ul>}
              </div>

              {/* CTA */}
              <div className="px-6 pb-6" data-id="element-1749">
                <button onClick={() => navigate(program.route)} className="flex items-center justify-center gap-2 w-full py-3 bg-carbon-bright text-white text-sm font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-sm shadow-carbon-bright/20" data-id="element-1750">

                  View Program
                  <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-1751" />
                </button>
              </div>
            </motion.article>)}
        </div>
      </div>
    </section>;
}
/* ════════════════════════════════════════
   3. FEATURED COURSE — BANKING PROGRAM
════════════════════════════════════════ */
function FeaturedCourseSection() {
  const [activeTab, setActiveTab] = useState<'overview' | 'modules' | 'delivery'>('overview');
  const banking = programs.find(p => p.id === 'banking')!;
  const tabs = [{
    id: 'overview',
    label: 'Overview & Outcomes'
  }, {
    id: 'modules',
    label: 'Module Breakdown'
  }, {
    id: 'delivery',
    label: 'Delivery & Certification'
  }] as const;
  return <section className="w-full bg-white py-20 sm:py-28 border-t border-carbon-geo" data-id="element-1752">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1753">
        <FadeIn data-id="element-1754">
          <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-1755">
            Featured Program
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-deep tracking-tight max-w-3xl" data-id="element-1756">
            Banking & Financial Institutions Program
          </h2>
          <p className="mt-4 text-lg text-carbon-mid max-w-2xl" data-id="element-1757">
            The most comprehensive climate certification for banks, DFIs, and
            investment firms operating under PCAF, IFRS S2, and Basel climate
            risk frameworks.
          </p>
        </FadeIn>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8 items-start" data-id="element-1758">
          {/* Left: Tabbed content */}
          <div className="lg:col-span-2" data-id="element-1759">
            <div className="flex border-b border-carbon-geo mb-8" data-id="element-1760">
              {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors -mb-px ${activeTab === tab.id ? 'border-carbon-bright text-carbon-deep' : 'border-transparent text-carbon-mid hover:text-carbon-deep'}`} data-id="element-1761">

                  {tab.label}
                </button>)}
            </div>

            {activeTab === 'overview' && <motion.div key="overview" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} data-id="element-1762">

                <div className="mb-8" data-id="element-1763">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-mid/60 mb-4" data-id="element-1764">
                    Who This Is For
                  </h3>
                  <p className="text-base text-carbon-mid leading-relaxed" data-id="element-1765">
                    Commercial banks, development finance institutions,
                    investment managers, insurance companies, and credit unions.
                    Specifically designed for credit officers, risk managers,
                    sustainability teams, CFOs, and board members responsible
                    for climate-related financial disclosures.
                  </p>
                </div>

                <div className="mb-8" data-id="element-1766">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-mid/60 mb-4" data-id="element-1767">
                    Regulatory Alignment
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-1768">
                    {banking.regulatoryAlignment.map(r => <span key={r} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-1769">

                        <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-1770" />
                        {r}
                      </span>)}
                  </div>
                </div>

                <div className="mb-8" data-id="element-1771">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-mid/60 mb-4" data-id="element-1772">
                    Learning Outcomes
                  </h3>
                  <ul className="space-y-3" data-id="element-1773">
                    {['Calculate financed emissions using PCAF attribution methods across asset classes', 'Produce IFRS S1/S2 compliant climate disclosures for financial institutions', 'Integrate climate risk into credit underwriting and loan assessment processes', 'Conduct basic climate stress tests and transition risk scenario analysis', 'Identify and avoid greenwashing in green bond and sustainable finance products', 'Establish board-level climate governance and oversight frameworks'].map((outcome, i) => <li key={i} className="flex items-start gap-3" data-id="element-1774">
                        <div className="w-5 h-5 rounded-lg bg-carbon-bright/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1775">
                          <CheckIcon className="w-3 h-3 text-carbon-bright" strokeWidth={3} data-id="element-1776" />

                        </div>
                        <span className="text-sm text-carbon-mid leading-snug" data-id="element-1777">
                          {outcome}
                        </span>
                      </li>)}
                  </ul>
                </div>

                <div data-id="element-1778">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-mid/60 mb-4" data-id="element-1779">
                    Case Studies
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1780">
                    <div className="rounded-2xl border border-carbon-geo p-5 bg-white" data-id="element-1781">
                      <p className="text-sm font-bold text-carbon-deep mb-1" data-id="element-1782">
                        East African DFI
                      </p>
                      <p className="text-sm text-carbon-mid leading-snug" data-id="element-1783">
                        How a development finance institution built
                        PCAF-compliant financed emissions reporting for a $2.4B
                        loan portfolio.
                      </p>
                    </div>
                    <div className="rounded-2xl border border-carbon-geo p-5 bg-white" data-id="element-1784">
                      <p className="text-sm font-bold text-carbon-deep mb-1" data-id="element-1785">
                        Commercial Bank Uganda
                      </p>
                      <p className="text-sm text-carbon-mid leading-snug" data-id="element-1786">
                        Integrating climate risk into credit assessment for
                        agricultural and energy sector lending under Basel
                        climate guidance.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>}

            {activeTab === 'modules' && <motion.div key="modules" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} data-id="element-1787">

                <div className="space-y-3" data-id="element-1788">
                  {[{
                num: '01',
                title: 'Financed Emissions & PCAF Methodology',
                desc: 'Introduction to the PCAF Standard, asset class coverage, data quality scoring, and attribution methods for loans, bonds, and equity.',
                duration: '4 hours'
              }, {
                num: '02',
                title: 'Portfolio Carbon Accounting & Attribution',
                desc: 'Practical calculation of portfolio-level emissions. Weighted average carbon intensity, absolute emissions, and sector benchmarking.',
                duration: '4 hours'
              }, {
                num: '03',
                title: 'Loan Book Emissions Calculation',
                desc: 'Applying PCAF to commercial real estate, corporate loans, project finance, and SME lending. Data gap management strategies.',
                duration: '4 hours'
              }, {
                num: '04',
                title: 'IFRS S1 & S2 for Financial Institutions',
                desc: 'Disclosure requirements, governance structures, strategy, risk management, and metrics under ISSB standards.',
                duration: '6 hours'
              }, {
                num: '05',
                title: 'Climate Risk in Credit Assessment',
                desc: 'Integrating physical and transition risk into credit underwriting, loan covenants, and sector exposure limits.',
                duration: '6 hours'
              }, {
                num: '06',
                title: 'Stress Testing & Transition Risk Modelling',
                desc: 'NGFS scenario analysis, orderly vs. disorderly transition scenarios, and basic stress test methodology.',
                duration: '4 hours'
              }, {
                num: '07',
                title: 'Sustainable Finance & Green Taxonomy',
                desc: 'EU Green Taxonomy alignment, green bond principles, sustainability-linked loans, and greenwashing risk management.',
                duration: '6 hours'
              }, {
                num: '08',
                title: 'Green Bonds & Avoiding Greenwashing',
                desc: 'ICMA Green Bond Principles, use of proceeds verification, impact reporting, and regulatory enforcement trends.',
                duration: '6 hours'
              }].map(mod => <div key={mod.num} className="flex gap-5 p-5 rounded-2xl border border-carbon-geo bg-white" data-id="element-1789">

                      <span className="text-2xl font-bold text-carbon-geo leading-none flex-shrink-0 w-8" data-id="element-1790">
                        {mod.num}
                      </span>
                      <div className="flex-1 min-w-0" data-id="element-1791">
                        <div className="flex items-start justify-between gap-3" data-id="element-1792">
                          <h4 className="text-sm font-bold text-carbon-deep" data-id="element-1793">
                            {mod.title}
                          </h4>
                          <span className="text-[10px] font-semibold text-carbon-mid/60 flex-shrink-0 flex items-center gap-1" data-id="element-1794">
                            <ClockIcon className="w-3 h-3" data-id="element-1795" />
                            {mod.duration}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-carbon-mid leading-relaxed" data-id="element-1796">
                          {mod.desc}
                        </p>
                      </div>
                    </div>)}
                </div>
              </motion.div>}

            {activeTab === 'delivery' && <motion.div key="delivery" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} data-id="element-1797">

                <div className="space-y-6" data-id="element-1798">
                  <div data-id="element-1799">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-mid/60 mb-4" data-id="element-1800">
                      Delivery Formats
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1801">
                      {[{
                    label: 'In-Person',
                    desc: 'Kampala, Uganda. Cohort-based, 5 consecutive days. Includes printed materials and networking sessions.'
                  }, {
                    label: 'Online',
                    desc: 'Self-paced with live instructor Q&A sessions. Access to recorded modules for 90 days post-enrollment.'
                  }, {
                    label: 'Hybrid',
                    desc: 'Blended format for corporate cohorts. 3 days in-person, 2 days online. Recommended for teams of 5+.'
                  }].map(format => <div key={format.label} className="rounded-2xl border border-carbon-geo p-5 bg-white" data-id="element-1802">

                          <p className="text-sm font-bold text-carbon-deep mb-2" data-id="element-1803">
                            {format.label}
                          </p>
                          <p className="text-xs text-carbon-mid leading-relaxed" data-id="element-1804">
                            {format.desc}
                          </p>
                        </div>)}
                    </div>
                  </div>

                  <div data-id="element-1805">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-mid/60 mb-4" data-id="element-1806">
                      Certification Details
                    </h3>
                    <div className="rounded-2xl border border-carbon-geo p-6 bg-white" data-id="element-1807">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1808">
                        <div data-id="element-1809">
                          <p className="text-xs font-bold text-carbon-mid/60 uppercase tracking-wider mb-2" data-id="element-1810">
                            Certificate Issued
                          </p>
                          <p className="text-sm text-carbon-deep font-semibold" data-id="element-1811">
                            CarbonSafe Certified Climate Finance Professional
                            (CCFP)
                          </p>
                        </div>
                        <div data-id="element-1812">
                          <p className="text-xs font-bold text-carbon-mid/60 uppercase tracking-wider mb-2" data-id="element-1813">
                            Assessment
                          </p>
                          <p className="text-sm text-carbon-mid" data-id="element-1814">
                            Written examination + practical case study. Minimum
                            70% pass mark.
                          </p>
                        </div>
                        <div data-id="element-1815">
                          <p className="text-xs font-bold text-carbon-mid/60 uppercase tracking-wider mb-2" data-id="element-1816">
                            Validity
                          </p>
                          <p className="text-sm text-carbon-mid" data-id="element-1817">
                            3 years. Annual CPD requirement of 8 hours for
                            renewal.
                          </p>
                        </div>
                        <div data-id="element-1818">
                          <p className="text-xs font-bold text-carbon-mid/60 uppercase tracking-wider mb-2" data-id="element-1819">
                            CPD Recognition
                          </p>
                          <p className="text-sm text-carbon-mid" data-id="element-1820">
                            40 CPD hours. Recognized for HR compliance
                            reporting.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>}
          </div>

          {/* Right: Sticky enrollment panel */}
          <div className="lg:sticky lg:top-24" data-id="element-1821">
            <div className="rounded-2xl border border-carbon-geo bg-white overflow-hidden shadow-lg shadow-carbon-deep/5" data-id="element-1822">
              <div className="bg-carbon-deep p-5 rounded-t-2xl" data-id="element-1823">
                <p className="text-xs font-bold uppercase tracking-widest text-carbon-bright mb-1" data-id="element-1824">
                  Banking & Finance Program
                </p>
                <p className="text-2xl font-bold text-white" data-id="element-1825">$1,800</p>
                <p className="text-xs text-white/50 mt-0.5" data-id="element-1826">per participant</p>
              </div>

              <div className="p-5 space-y-4 border-b border-carbon-geo" data-id="element-1827">
                <div className="flex items-center justify-between text-sm" data-id="element-1828">
                  <span className="text-carbon-mid flex items-center gap-2" data-id="element-1829">
                    <ClockIcon className="w-4 h-4" data-id="element-1830" /> Duration
                  </span>
                  <span className="font-bold text-carbon-deep" data-id="element-1831">5 days</span>
                </div>
                <div className="flex items-center justify-between text-sm" data-id="element-1832">
                  <span className="text-carbon-mid flex items-center gap-2" data-id="element-1833">
                    <GraduationCapIcon className="w-4 h-4" data-id="element-1834" /> Level
                  </span>
                  <span className="font-bold text-carbon-deep" data-id="element-1835">Advanced</span>
                </div>
                <div className="flex items-center justify-between text-sm" data-id="element-1836">
                  <span className="text-carbon-mid flex items-center gap-2" data-id="element-1837">
                    <AwardIcon className="w-4 h-4" data-id="element-1838" /> CPD Hours
                  </span>
                  <span className="font-bold text-carbon-deep" data-id="element-1839">40 hours</span>
                </div>
                <div className="flex items-center justify-between text-sm" data-id="element-1840">
                  <span className="text-carbon-mid flex items-center gap-2" data-id="element-1841">
                    <UsersIcon className="w-4 h-4" data-id="element-1842" /> Corporate Discount
                  </span>
                  <span className="font-bold text-carbon-bright" data-id="element-1843">5+ seats</span>
                </div>
              </div>

              <div className="p-5 space-y-3" data-id="element-1844">
                <a href={MAILTO.enrollCourse} className="flex items-center justify-center gap-2 w-full py-3.5 bg-carbon-bright text-white text-sm font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-sm shadow-carbon-bright/20" data-id="element-1845">

                  Enroll Now <ArrowRightIcon className="w-4 h-4" data-id="element-1846" />
                </a>
                <a href={MAILTO.courseSyllabus} className="flex items-center justify-center gap-2 w-full py-3 border-2 border-carbon-bright text-carbon-deep text-sm font-semibold rounded-lg hover:bg-carbon-bright/5 transition-colors" data-id="element-1847">

                  <DownloadIcon className="w-4 h-4" data-id="element-1848" /> Download Syllabus
                </a>
                <a href={MAILTO.corporateCohort} className="flex items-center justify-center gap-2 w-full py-3 border border-carbon-geo text-carbon-mid text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1849">

                  Request Corporate Cohort
                </a>
              </div>

              <div className="px-5 pb-5" data-id="element-1850">
                <p className="text-[10px] text-carbon-mid/60 text-center leading-relaxed" data-id="element-1851">
                  Corporate cohort pricing available for 5+ participants.
                  Contact us for custom scheduling and on-site delivery.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
/* ════════════════════════════════════════
   4. TRUST & AUTHORITY
════════════════════════════════════════ */
function TrustSection() {
  const frameworks = ['GHG Protocol', 'IFRS S1/S2', 'PCAF Standard', 'EU CSRD / ESRS', 'CBAM', 'EUDR', 'Basel IV', 'TCFD', 'Verra VCS', 'Gold Standard', 'ISO 14064', 'UNFCCC Article 6'];
  const testimonials = [{
    quote: 'The Banking & Finance program gave our credit team the exact framework we needed to integrate climate risk into our loan assessment process. Audit-ready from day one.',
    author: 'Head of Risk, East African Commercial Bank',
    role: 'Corporate Enrollment, 12 participants'
  }, {
    quote: "CarbonSafe's certification is the only program I've found that actually addresses PCAF methodology with practical case studies. Not theory — real calculation exercises.",
    author: 'Chief Sustainability Officer, Development Finance Institution',
    role: 'Executive Enrollment'
  }, {
    quote: 'We enrolled our entire compliance team before our CSRD readiness audit. The training directly informed our double materiality assessment process.',
    author: 'Group Compliance Director, Manufacturing Conglomerate',
    role: 'Corporate Cohort, 8 participants'
  }];
  return <section className="w-full bg-white py-20 sm:py-28 border-t border-carbon-geo" data-id="element-1852">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1853">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-1854">
          <FadeIn data-id="element-1855">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-1856">
              Regulatory Coverage
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-deep tracking-tight" data-id="element-1857">
              Every major framework.{' '}
              <span className="text-carbon-bright" data-id="element-1858">
                One certification body.
              </span>
            </h2>
            <p className="mt-4 text-base text-carbon-mid leading-relaxed" data-id="element-1859">
              CarbonSafe certifications are aligned with the full spectrum of
              international and regional climate and sustainability reporting
              frameworks.
            </p>
            <div className="mt-8 flex flex-wrap gap-2" data-id="element-1860">
              {frameworks.map(f => <span key={f} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-1861">

                  <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-1862" />
                  {f}
                </span>)}
            </div>

            <div className="mt-10 grid grid-cols-3 gap-5 pt-8 border-t border-carbon-geo" data-id="element-1863">
              <div data-id="element-1864">
                <p className="text-4xl font-bold text-carbon-bright" data-id="element-1865">6</p>
                <p className="text-sm text-carbon-mid mt-1 font-medium" data-id="element-1866">
                  Certification programs
                </p>
              </div>
              <div data-id="element-1867">
                <p className="text-4xl font-bold text-carbon-bright" data-id="element-1868">12</p>
                <p className="text-sm text-carbon-mid mt-1 font-medium" data-id="element-1869">
                  Frameworks covered
                </p>
              </div>
              <div data-id="element-1870">
                <p className="text-4xl font-bold text-carbon-bright" data-id="element-1871">2026</p>
                <p className="text-sm text-carbon-mid mt-1 font-medium" data-id="element-1872">
                  First cohort launching
                </p>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.15} data-id="element-1873">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-1874">
              Institutional Feedback
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-deep tracking-tight" data-id="element-1875">
              Trusted by compliance teams across East Africa
            </h2>
            <div className="mt-8 space-y-5" data-id="element-1876">
              {testimonials.map((t, i) => <div key={i} className="rounded-2xl border border-carbon-geo bg-white p-5" data-id="element-1877">

                  <p className="text-sm text-carbon-mid leading-relaxed italic" data-id="element-1878">
                    "{t.quote}"
                  </p>
                  <div className="mt-3 flex items-center gap-2" data-id="element-1879">
                    <span className="w-2 h-2 rounded-full bg-carbon-bright flex-shrink-0" data-id="element-1880" />
                    <div data-id="element-1881">
                      <p className="text-xs font-bold text-carbon-deep" data-id="element-1882">
                        {t.author}
                      </p>
                      <p className="text-[10px] text-carbon-mid/60 mt-0.5" data-id="element-1883">
                        {t.role}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}
/* ════════════════════════════════════════
   5. ENTERPRISE TRAINING (dark section — matches pattern)
════════════════════════════════════════ */
function EnterpriseSection() {
  const [form, setForm] = useState({
    institution: '',
    contact: '',
    email: '',
    program: '',
    participants: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const buildMailto = () => {
    const subject = encodeURIComponent('Enterprise Training Proposal Request');
    const body = encodeURIComponent(`Hello,\n\nI would like to request an enterprise training proposal for my institution.\n\nInstitution: ${form.institution}\nContact name & role: ${form.contact}\nEmail: ${form.email}\nProgram of interest: ${form.program}\nNumber of participants: ${form.participants}\n\nThank you`);
    return `mailto:operations@carbonsafe.earth?subject=${subject}&body=${body}`;
  };
  return <section className="w-full bg-carbon-deep py-20 sm:py-28" data-id="element-1884">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1885">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center" data-id="element-1886">
          <FadeIn data-id="element-1887">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/15 px-3 py-1 rounded-full mb-5" data-id="element-1888">
              Enterprise Training
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1889">
              Enterprise ESG Training for Banks & Institutions
            </h2>
            <div className="w-12 h-0.5 bg-carbon-bright mt-5 mb-6" data-id="element-1890" />
            <p className="text-base text-white/60 leading-relaxed" data-id="element-1891">
              For institutions requiring structured, organisation-wide climate
              competency. CarbonSafe delivers custom cohort training with
              regulatory-aligned certification reporting for HR and compliance
              teams.
            </p>

            <div className="mt-8 space-y-5" data-id="element-1892">
              {[{
              icon: <UsersIcon className="w-5 h-5" data-id="element-1893" />,
              title: 'Custom Cohort Programs',
              desc: 'Dedicated training cohorts for your team. Minimum 5 participants. Custom scheduling, on-site or remote delivery.'
            }, {
              icon: <BriefcaseIcon className="w-5 h-5" data-id="element-1894" />,
              title: 'Board-Level Programs',
              desc: 'Executive certification designed for directors and C-suite. Condensed format, governance-focused, litigation risk coverage.'
            }, {
              icon: <ShieldCheckIcon className="w-5 h-5" data-id="element-1895" />,
              title: 'Regulatory-Aligned Training',
              desc: "Programs mapped directly to your institution's regulatory obligations — PCAF, IFRS S2, Basel climate risk, CSRD."
            }, {
              icon: <FileTextIcon className="w-5 h-5" data-id="element-1896" />,
              title: 'Certification Reporting for HR/Compliance',
              desc: 'Structured completion records, CPD certificates, and audit-ready training logs for regulatory and HR compliance teams.'
            }].map((item, i) => <div key={i} className="flex items-start gap-4" data-id="element-1897">
                  <div className="w-10 h-10 rounded-xl bg-carbon-bright/15 flex items-center justify-center flex-shrink-0" data-id="element-1898">
                    <div className="text-carbon-bright" data-id="element-1899">{item.icon}</div>
                  </div>
                  <div data-id="element-1900">
                    <p className="text-sm font-bold text-white" data-id="element-1901">{item.title}</p>
                    <p className="text-sm text-white/50 mt-1 leading-relaxed" data-id="element-1902">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </FadeIn>

          <FadeIn delay={0.15} data-id="element-1903">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8" data-id="element-1904">
              <p className="text-xs font-bold uppercase tracking-widest text-carbon-bright mb-6" data-id="element-1905">
                Request Enterprise Proposal
              </p>
              <div className="space-y-4" data-id="element-1906">
                <div data-id="element-1907">
                  <label className="block text-xs font-semibold text-white/60 mb-1.5 uppercase tracking-wider" data-id="element-1908">
                    Institution Name
                  </label>
                  <input type="text" name="institution" value={form.institution} onChange={handleChange} placeholder="e.g. Stanbic Bank Uganda" className="w-full bg-white/5 border border-white/15 text-white placeholder-white/30 px-4 py-3 text-sm rounded-lg focus:outline-none focus:border-carbon-bright transition-colors" data-id="element-1909" />

                </div>
                <div data-id="element-1910">
                  <label className="block text-xs font-semibold text-white/60 mb-1.5 uppercase tracking-wider" data-id="element-1911">
                    Contact Name & Role
                  </label>
                  <input type="text" name="contact" value={form.contact} onChange={handleChange} placeholder="e.g. Jane Doe, Head of Compliance" className="w-full bg-white/5 border border-white/15 text-white placeholder-white/30 px-4 py-3 text-sm rounded-lg focus:outline-none focus:border-carbon-bright transition-colors" data-id="element-1912" />

                </div>
                <div data-id="element-1913">
                  <label className="block text-xs font-semibold text-white/60 mb-1.5 uppercase tracking-wider" data-id="element-1914">
                    Email Address
                  </label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="compliance@institution.com" className="w-full bg-white/5 border border-white/15 text-white placeholder-white/30 px-4 py-3 text-sm rounded-lg focus:outline-none focus:border-carbon-bright transition-colors" data-id="element-1915" />

                </div>
                <div data-id="element-1916">
                  <label className="block text-xs font-semibold text-white/60 mb-1.5 uppercase tracking-wider" data-id="element-1917">
                    Program of Interest
                  </label>
                  <select name="program" value={form.program} onChange={handleChange} className="w-full bg-white/5 border border-white/15 text-white/70 px-4 py-3 text-sm rounded-lg focus:outline-none focus:border-carbon-bright transition-colors" data-id="element-1918">

                    <option value="" className="bg-carbon-deep" data-id="element-1919">
                      Select a program
                    </option>
                    <option value="ESG Foundations" className="bg-carbon-deep" data-id="element-1920">
                      ESG Foundations
                    </option>
                    <option value="Banking & Financial Institutions" className="bg-carbon-deep" data-id="element-1921">

                      Banking & Financial Institutions
                    </option>
                    <option value="Oil & Gas Climate Compliance" className="bg-carbon-deep" data-id="element-1922">

                      Oil & Gas Climate Compliance
                    </option>
                    <option value="Manufacturing & Export" className="bg-carbon-deep" data-id="element-1923">

                      Manufacturing & Export
                    </option>
                    <option value="Agriculture & Forestry (AFOLU)" className="bg-carbon-deep" data-id="element-1924">

                      Agriculture & Forestry (AFOLU)
                    </option>
                    <option value="Executive & Board Certification" className="bg-carbon-deep" data-id="element-1925">

                      Executive & Board Certification
                    </option>
                    <option value="Custom Multi-Program" className="bg-carbon-deep" data-id="element-1926">

                      Custom Multi-Program
                    </option>
                  </select>
                </div>
                <div data-id="element-1927">
                  <label className="block text-xs font-semibold text-white/60 mb-1.5 uppercase tracking-wider" data-id="element-1928">
                    Number of Participants
                  </label>
                  <input type="number" name="participants" value={form.participants} onChange={handleChange} placeholder="e.g. 15" min="1" className="w-full bg-white/5 border border-white/15 text-white placeholder-white/30 px-4 py-3 text-sm rounded-lg focus:outline-none focus:border-carbon-bright transition-colors" data-id="element-1929" />

                </div>
                <a href={buildMailto()} className="flex items-center justify-center gap-2 w-full py-4 bg-carbon-bright text-white text-sm font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-black/20 mt-2" data-id="element-1930">

                  Request Enterprise Proposal
                  <ArrowRightIcon className="w-4 h-4" data-id="element-1931" />
                </a>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}
/* ════════════════════════════════════════
   6. RESOURCES
════════════════════════════════════════ */
function ResourcesSection() {
  return <section className="w-full bg-white py-16 sm:py-20 border-t border-carbon-geo" data-id="element-1932">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1933">
        <FadeIn className="mb-10" data-id="element-1934">
          <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-1935">
            Resources
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-carbon-deep tracking-tight" data-id="element-1936">
            Institutional Resources
          </h2>
        </FadeIn>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-1937">
          <FadeIn delay={0.05} data-id="element-1938">
            <div className="rounded-2xl border border-carbon-geo p-6 bg-white h-full flex flex-col" data-id="element-1939">
              <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-4" data-id="element-1940">
                <FileTextIcon className="w-6 h-6 text-carbon-bright" data-id="element-1941" />
              </div>
              <h3 className="text-base font-bold text-carbon-deep mb-2" data-id="element-1942">
                Whitepaper
              </h3>
              <p className="text-sm text-carbon-mid leading-relaxed flex-1" data-id="element-1943">
                "Climate Risk for African Banks" — A regulatory briefing on
                PCAF, Basel climate guidance, and IFRS S2 implementation for
                East African financial institutions.
              </p>
              <a href={MAILTO.trainingBrochure} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-carbon-bright hover:text-carbon-mid transition-colors" data-id="element-1944">

                <DownloadIcon className="w-4 h-4" data-id="element-1945" /> Download Whitepaper
              </a>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-1946">
            <div className="rounded-2xl border border-carbon-geo p-6 bg-white h-full flex flex-col" data-id="element-1947">
              <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-4" data-id="element-1948">
                <BookOpenIcon className="w-6 h-6 text-carbon-bright" data-id="element-1949" />
              </div>
              <h3 className="text-base font-bold text-carbon-deep mb-2" data-id="element-1950">
                Course Brochure
              </h3>
              <p className="text-sm text-carbon-mid leading-relaxed flex-1" data-id="element-1951">
                Full program catalog with module breakdowns, regulatory
                alignment maps, pricing, and corporate enrollment terms for all
                six certification programs.
              </p>
              <a href={MAILTO.trainingBrochure} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-carbon-bright hover:text-carbon-mid transition-colors" data-id="element-1952">

                <DownloadIcon className="w-4 h-4" data-id="element-1953" /> Download Brochure
              </a>
            </div>
          </FadeIn>

          <FadeIn delay={0.15} data-id="element-1954">
            <div className="rounded-2xl border border-carbon-geo p-6 bg-white h-full flex flex-col" data-id="element-1955">
              <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-4" data-id="element-1956">
                <AlertTriangleIcon className="w-6 h-6 text-carbon-bright" data-id="element-1957" />
              </div>
              <h3 className="text-base font-bold text-carbon-deep mb-2" data-id="element-1958">
                Regulatory Brief
              </h3>
              <p className="text-sm text-carbon-mid leading-relaxed flex-1" data-id="element-1959">
                Subscribe to CarbonSafe's monthly regulatory compliance updates.
                CSRD, CBAM, PCAF, and Uganda Climate Change Act developments
                delivered to your inbox.
              </p>
              <a href={MAILTO.regulatoryUpdates} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-carbon-bright hover:text-carbon-mid transition-colors" data-id="element-1960">

                <MailIcon className="w-4 h-4" data-id="element-1961" /> Subscribe to Updates
              </a>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-1962">
            <div className="rounded-2xl border border-carbon-geo p-6 bg-white h-full flex flex-col" data-id="element-1963">
              <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-4" data-id="element-1964">
                <PhoneIcon className="w-6 h-6 text-carbon-bright" data-id="element-1965" />
              </div>
              <h3 className="text-base font-bold text-carbon-deep mb-2" data-id="element-1966">
                Corporate Enrollment
              </h3>
              <p className="text-sm text-carbon-mid leading-relaxed flex-1" data-id="element-1967">
                Speak directly with our institutional training team. Custom
                cohort scheduling, volume pricing, and regulatory-aligned
                training plans for your institution.
              </p>
              <a href={MAILTO.trainingContact} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-carbon-bright hover:text-carbon-mid transition-colors" data-id="element-1968">

                <MailIcon className="w-4 h-4" data-id="element-1969" /> Contact Training Team
              </a>
            </div>
          </FadeIn>
        </div>

        {/* Bottom contact bar */}
        <div className="mt-10 rounded-2xl border border-carbon-geo bg-white p-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-1970">
          <div data-id="element-1971">
            <p className="text-sm font-bold text-carbon-deep" data-id="element-1972">
              Institutional Enrollment Enquiries
            </p>
            <p className="text-xs text-carbon-mid mt-0.5" data-id="element-1973">
              Regulatory references: IFRS S1/S2 · CSRD ESRS E1 · PCAF Standard
              v1.0 · Basel BCBS 530 · Uganda Climate Change Act 2021
            </p>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0" data-id="element-1974">
            <a href="tel:+256776007755" className="text-sm font-semibold text-carbon-deep hover:text-carbon-bright transition-colors flex items-center gap-1.5" data-id="element-1975">

              <PhoneIcon className="w-4 h-4" data-id="element-1976" /> +256 776 007755
            </a>
            <a href={MAILTO.trainingContact} className="inline-flex items-center gap-2 px-5 py-2.5 bg-carbon-bright text-white text-sm font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-sm shadow-carbon-bright/20" data-id="element-1977">

              <MailIcon className="w-4 h-4" data-id="element-1978" /> operations@carbonsafe.earth
            </a>
          </div>
        </div>
      </div>
    </section>;
}
/* ════════════════════════════════════════
   7. CTA — matches DMRVPageTemplate pattern
════════════════════════════════════════ */
function CTASection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section ref={ref} className="w-full py-20 sm:py-28 bg-white" data-id="element-1979">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1980">
        <motion.div className="relative overflow-hidden rounded-3xl px-8 py-16 sm:px-16 sm:py-20 text-center" style={{
        background: 'linear-gradient(135deg, #2D6A4F 0%, #1B4332 100%)'
      }} initial={{
        opacity: 0,
        y: 40
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-1981">

          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-10" style={{
          background: 'radial-gradient(circle, #52B788, transparent)'
        }} aria-hidden="true" data-id="element-1982" />

          <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-10" style={{
          background: 'radial-gradient(circle, #6BCB77, transparent)'
        }} aria-hidden="true" data-id="element-1983" />

          <h2 className="relative text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight max-w-3xl mx-auto" data-id="element-1984">
            Build certified climate competency.
            <br data-id="element-1985" />
            <span className="text-carbon-bright" data-id="element-1986">
              Enroll your institution today.
            </span>
          </h2>
          <p className="relative mt-5 text-lg text-white/70 max-w-xl mx-auto" data-id="element-1987">
            Join banks, oil & gas firms, and manufacturers across East Africa
            building audit-defensible ESG competency with CarbonSafe
            certification programs.
          </p>
          <div className="relative mt-10 flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1988">
            <a href={MAILTO.corporateTraining} className="inline-flex items-center gap-2 px-8 py-4 bg-carbon-bright text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-black/20" data-id="element-1989">

              Request Corporate Training
              <ArrowRightIcon className="w-4 h-4" data-id="element-1990" />
            </a>
            <a href={MAILTO.trainingBrochure} className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-lg hover:bg-white/10 transition-colors" data-id="element-1991">

              Download Course Brochure
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ════════════════════════════════════════
   MAIN EXPORT
════════════════════════════════════════ */
export function ESGCourses() {
  return <>
      <HeroSection data-id="element-1992" />
      <ProgramsSection data-id="element-1993" />
      <FeaturedCourseSection data-id="element-1994" />
      <TrustSection data-id="element-1995" />
      <EnterpriseSection data-id="element-1996" />
      <ResourcesSection data-id="element-1997" />
      <CTASection data-id="element-1998" />
      <Footer data-id="element-1999" />
    </>;
}